
import { useTranslations } from "use-intl";
import { Link } from "@/i18n/navigation";
import { useRouter } from "@/i18n/navigation";
import { isAdminRole } from "@examora/shared/auth/roles";
import { useLogout } from "@/features/auth/hooks/use-logout";
import { Button } from "@/components/ui/button";
import { Dropdown } from "@/components/ui/dropdown";
import { LocaleSwitcher } from "./locale-switcher";
import { MockLanguageSwitcher } from "./mock-language-switcher";
import { ThemeToggle } from "./theme-toggle";
import { SectionLink } from "./section-link";
import { LogoMark } from "./logo-mark";
import { NotificationBell } from "@/features/engagement/notification-bell";

type SiteHeaderUser = {
  displayName: string;
  role: string;
} | null;

/**
 * Nav links point to in-page anchors on the landing page (#why, #how-it-works,
 * #features) rather than separate routes — those routes don't exist yet, and
 * a nav link to a 404 is exactly the kind of broken-navigation pattern the
 * Phase 0 audit flagged in the original prototype. When those sections
 * become real standalone pages, this becomes a plain href swap.
 *
 * `user` is optional and comes from the session (each layout that renders
 * this reads useSession() and passes the result down) — this component
 * itself never re-checks auth, it just renders what it was given. Without
 * this, the header showed "Login"/"Register" unconditionally even to an
 * already-authenticated visitor, giving no visual confirmation that login
 * actually worked — confusing enough that a user reported it as "nothing
 * happens after I log in" when the real issue was just this header.
 */
export function SiteHeader({
  user = null,
  inApp = false,
}: {
  user?: SiteHeaderUser;
  /** Inside the student app (sidebar + phone bottom nav already lead home): keep the dashboard link quiet. */
  inApp?: boolean;
}) {
  const t = useTranslations("common");
  const router = useRouter();

  const handleLogout = useLogout("/");

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-md">
      <div className="examora-container flex h-16 items-center justify-between gap-4">
        <Link
          href="/"
          className="flex items-center gap-2 text-lg font-semibold tracking-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
        >
          <LogoMark className="h-7 w-7" />
          {/* Icon only on the narrowest phones, so the header's buttons still fit in one row. */}
          <span className="hidden min-[400px]:inline">{t("appName")}</span>
        </Link>

        {!user && (
          <nav
            aria-label={t("nav.menu")}
            className="hidden items-center gap-6 text-sm text-muted-foreground lg:flex"
          >
            <SectionLink section="product-preview" className="transition-colors hover:text-foreground">
              {t("nav.platform")}
            </SectionLink>
            <SectionLink section="features" className="transition-colors hover:text-foreground">
              {t("nav.features")}
            </SectionLink>
            <Link href="/pricing" className="transition-colors hover:text-foreground">
              {t("nav.pricing")}
            </Link>
            <SectionLink section="faq" className="transition-colors hover:text-foreground">
              {t("nav.faq")}
            </SectionLink>
            <Link href="/contact" className="transition-colors hover:text-foreground">
              {t("nav.contact")}
            </Link>
          </nav>
        )}

        <div className="flex items-center gap-1.5 sm:gap-3">
          {user && <NotificationBell />}
          {/* On the very narrowest phones (< 360 px) a logged-out visitor's row is Login + Register first. */}
          <div className={user ? "contents" : "hidden min-[360px]:contents"}>
            <ThemeToggle />
          </div>
          {/* Logged-in students filter mocks by content language here (interface
             language moved to their Profile settings); logged-out visitors
             (landing/marketing page, no profile to store a preference in yet)
             keep the plain interface-language switcher. */}
          {user ? <MockLanguageSwitcher /> : <LocaleSwitcher />}
          {user ? (
            <>
              {isAdminRole(user.role) ? (
                <Link href="/admin" className="hidden sm:block">
                  <Button variant="ghost" size="sm">
                    {t("nav.admin")}
                  </Button>
                </Link>
              ) : null}
              {inApp ? (
                <Link href="/dashboard" className="hidden sm:block">
                  <Button variant="ghost" size="sm">
                    {t("nav.cabinet")}
                  </Button>
                </Link>
              ) : (
                // Public pages: a signed-in visitor must always see the way back to their account, phones too.
                <Link href="/dashboard">
                  <Button variant="primary" size="sm" className="whitespace-nowrap px-2.5 text-[13px] sm:px-3 sm:text-sm">
                    <span className="sm:hidden">{t("nav.cabinetShort")}</span>
                    <span className="hidden sm:inline">{t("nav.cabinet")}</span>
                  </Button>
                </Link>
              )}
              <Dropdown
                align="end"
                trigger={
                  <span className="flex items-center gap-2">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                      {user.displayName.charAt(0).toUpperCase()}
                    </span>
                    <span className="hidden max-w-[8rem] truncate sm:inline">{user.displayName}</span>
                  </span>
                }
                items={[
                  { label: t("nav.profile"), onClick: () => router.push("/profile") },
                  { label: t("nav.cabinet"), onClick: () => router.push("/dashboard") },
                  { label: t("nav.logout"), onClick: handleLogout, destructive: true },
                ]}
              />
            </>
          ) : (
            <>
              {/* Visible on phones too — a returning user must be able to sign in from the landing page. */}
              <Link href="/login">
                <Button variant="ghost" size="sm" className="px-2 sm:px-3">
                  {t("nav.login")}
                </Button>
              </Link>
              <Link href="/register">
                <Button variant="primary" size="sm" className="whitespace-nowrap px-2.5 text-[13px] sm:px-3 sm:text-sm">
                  {t("nav.register")}
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
